import { Collection, Feature, Map } from "ol";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { useEffect, useState } from "react";
import { useDraw } from "./interactions/useDraw";
import { useModify } from "./interactions/useModify";
import { useMouseEvents } from "./interactions/useMouseEvents";
import { useSelect } from "./interactions/useSelect";
import { useSnap } from "./interactions/useSnap";
import { useBBoxLayer } from "./highlight/useBBoxLayer";
import { useFeatureOverlay } from "./highlight/useFeatureOverlay";
import { FeatureLike } from "ol/Feature";

export function useInteractions(
	map: Map | null,
	source: VectorSource,
	layer: VectorLayer<VectorSource>,
	isSelectable: (feature: FeatureLike) => boolean,
	onUpdate: (features: Feature[]) => void,
) {
	// Only the shapes on show can be dragged or snapped to. Kept in step with
	// shapes coming and going, but not with edits — rebuilding mid-drag would
	// drop the shape being dragged.
	const [shownFeatures] = useState(() => new Collection<Feature>());
	useEffect(() => {
		const sync = () => {
			shownFeatures.clear();
			shownFeatures.extend(source.getFeatures().filter(isSelectable));
		};
		sync();
		source.on(["addfeature", "removefeature", "clear"], sync);
		return () => {
			source.un(["addfeature", "removefeature", "clear"], sync);
		};
	}, [source, isSelectable]);

	useSnap(map, shownFeatures);

	const draw = useDraw(map, source, onUpdate);
	const [drawEnabled, setDrawEnabled] = useState(false);
	useEffect(() => {
		draw.setActive(drawEnabled);
	}, [drawEnabled]);

	const modify = useModify(map, shownFeatures, onUpdate);
	const [modifyEnabled, setModifyEnabled] = useState(false);
	useEffect(() => {
		modify.setActive(modifyEnabled);
	}, [modifyEnabled]);

	// Marking points of interest has no interaction of its own here, but it is
	// still an edit mode — selection and hover stay off while it is on
	const [poiEnabled, setPoiEnabled] = useState(false);

	const isEditing = drawEnabled || modifyEnabled || poiEnabled;

	const [focus, setFocus] = useState<Feature | null>(null);
	const { focusOverlayElRef } = useFeatureOverlay(map, focus);
	useBBoxLayer(map, focus);

	const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
	useEffect(() => {
		setFocus(selectedFeature);
	}, [selectedFeature]);

	const select = useSelect(map, layer, isSelectable, setSelectedFeature);
	useEffect(() => {
		// disable selection in edit mode
		select.setActive(!isEditing);
	}, [select, isEditing]);

	useMouseEvents(map, (e) => {
		if (!map) return;
		// disable hover when a feature is selected or in edit mode
		if (selectedFeature) return;
		if (draw.getActive() || modify.getActive()) return;

		const features = map.getFeaturesAtPixel(e.pixel, {
			layerFilter: (l) => l === layer,
		});
		const feature = features[0];
		if (feature instanceof Feature && isSelectable(feature)) {
			setFocus(feature);
		} else {
			setFocus(null);
		}
	});

	useEffect(() => {
		setFocus(null);
	}, [isSelectable]);

	const [isDeleting, setIsDeleting] = useState(false);

	return {
		draw,
		modify,
		select,
		isEditing,
		isDeleting,
		setIsDeleting,
		drawEnabled,
		setDrawEnabled,
		modifyEnabled,
		setModifyEnabled,
		poiEnabled,
		setPoiEnabled,
		selectedFeature,
		setSelectedFeature,
		highlightedFeature: focus,
		overlayElRef: focusOverlayElRef,
	};
}
